import { client } from './client';
import { ENDPOINTS, LOCATION_CONFIG } from '../config';
import { toast } from 'react-toastify';
import { TOAST_CONFIG } from '../config/toastConfig';

class LocationTrackingService {
  constructor() {
    this.isTracking = false;
    this.activeTrips = new Set();
    this.activeTripMeta = new Map();
    this.intervalId = null;
    this.locationUpdatePromise = null;
    this.isAndroidWebView = !!window.Android; // Check if running in Android WebView
    
    // Restore persisted active trips if any
    try {
      const savedTrips = localStorage.getItem('activeTripIds');
      if (savedTrips) {
        const parsed = JSON.parse(savedTrips);
        if (Array.isArray(parsed)) {
          parsed.forEach((tripId) => this.activeTrips.add(tripId));
        }
      }
      const savedMeta = localStorage.getItem('activeTripMeta');
      if (savedMeta) {
        const parsedMeta = JSON.parse(savedMeta);
        if (parsedMeta && typeof parsedMeta === 'object') {
          Object.entries(parsedMeta).forEach(([tripId, meta]) => {
            // Keys are trip ids (numeric) or trip codes like DL935839470; parseInt would turn codes into NaN.
            this.activeTripMeta.set(/^\d+$/.test(tripId) ? Number(tripId) : tripId, meta || {});
          });
        }
      }
    } catch (e) {
      console.error('Failed to restore active trips from storage:', e);
    }
    
    // Check localStorage first for permission state
    const storedPermission = localStorage.getItem('locationPermissionGranted');
    this.locationPermissionGranted = storedPermission === 'true';
    
    this.lastLocation = null;
    
    console.log('LocationTrackingService initialized:', {
      isAndroidWebView: this.isAndroidWebView,
      windowAndroid: window.Android,
      locationPermissionGranted: this.locationPermissionGranted
    });
  }

  // Helper method to show toast if enabled
  showToast(message, options = {}) {
    if (TOAST_CONFIG.enabled) {
      toast(message, options);
    }
  }

  // Initialize Android bridge if available
  initAndroidBridge() {
    if (this.isAndroidWebView) {
      // Check localStorage first for permission state
      const storedPermission = localStorage.getItem('locationPermissionGranted');
      if (storedPermission === 'true') {
        this.locationPermissionGranted = true;
        console.log('Location permission already granted (from localStorage)');
        return;
      }
      
      console.log('Initializing Android WebView location bridge');
      this.showToast('Initializing Android WebView location bridge', { autoClose: 2000 });
      
      // Set up listener for location updates from Android
      window.onLocationUpdate = (locationJson) => {
        console.log('Received location update from Android:', locationJson);
        this.showToast('Received location update from Android', { autoClose: 2000 });
        
        if (locationJson) {
          try {
            const location = typeof locationJson === 'string' ? JSON.parse(locationJson) : locationJson;
            console.log('Parsed location from Android:', location);
            this.showToast(`Location data: Lat: ${location.coords?.latitude || location.latitude}, Long: ${location.coords?.longitude || location.longitude}`, { autoClose: 3000 });
            
            // Normalize the location data to match our expected format
            const normalizedLocation = {
              latitude: location.coords?.latitude || location.latitude,
              longitude: location.coords?.longitude || location.longitude,
              accuracy: location.coords?.accuracy || location.accuracy,
              altitude: location.coords?.altitude || location.altitude,
              speed: location.coords?.speed || location.speed,
              heading: location.coords?.heading || location.heading,
              timestamp: location.timestamp || new Date().toISOString()
            };
            
            this.lastLocation = normalizedLocation;
            
            // Resolve any pending single-shot location request
            if (window.tempLocationResolver) {
              try {
                window.tempLocationResolver(normalizedLocation);
              } finally {
                delete window.tempLocationResolver;
                delete window.tempLocationRejecter;
              }
            }
            
            // Send location to server for all active trips
            if (this.activeTrips.size > 0) {
              const activeTripsList = Array.from(this.activeTrips);
              console.log('Sending location to server for active trips:', activeTripsList);
              this.showToast(`Sending location updates for ${activeTripsList.length} active trips`, { autoClose: 2000 });
              this.handleLocationUpdate(normalizedLocation);
            } else {
              console.log('No active trips to send location update');
              this.showToast('No active trips to send location update', { autoClose: 2000 });
            }
          } catch (error) {
            console.error('Error parsing location from Android:', error);
            this.showToast(`Error parsing location: ${error.message}`, { autoClose: 5000 });
          }
        }
      };

      // Set up listener for location permission results
      window.onLocationPermissionResult = (granted) => {
        console.log('Location permission result from Android:', granted);
        this.showToast(`Location permission result: ${granted}`, { autoClose: 2000 });
        
        this.locationPermissionGranted = granted === 'true';
        
        // Store in localStorage when granted
        if (this.locationPermissionGranted) {
          localStorage.setItem('locationPermissionGranted', 'true');
          console.log('Location permission granted, initializing location services');
          this.showToast('Location permission granted', { autoClose: 2000 });
          this.startLocationUpdates();
        } else {
          console.log('Location permission denied');
          this.showToast('Location permission denied', { autoClose: 5000 });
        }
      };

      // Check initial permission status
      if (window.Android && window.Android.isLocationPermissionGranted) {
        const permission = window.Android.isLocationPermissionGranted();
        console.log('Initial location permission check:', permission);
        this.locationPermissionGranted = permission === 'true';
        
        // Store in localStorage when granted
        if (this.locationPermissionGranted) {
          localStorage.setItem('locationPermissionGranted', 'true');
        }
      }
      
      // If there are persisted active trips, ensure tracking is started
      if (this.activeTrips.size > 0 && !this.isTracking) {
        this.startLocationUpdates();
      }
    } else {
      console.log('Not running in Android WebView, using web geolocation API');
      // If there are persisted active trips, ensure tracking is started
      if (this.activeTrips.size > 0 && !this.isTracking) {
        this.startLocationUpdates();
      }
    }
  }

  // Check if current user is a driver (from localStorage)
  isDriverUser() {
    try {
      const rawUserInfo = localStorage.getItem('userInfo');
      let userType = null;
      if (rawUserInfo) {
        const user = JSON.parse(rawUserInfo);
        userType = user?.userType || null;
      }
      // Fallback to userType stored separately in localStorage
      if (!userType) {
        const rawUserType = localStorage.getItem('userType');
        if (rawUserType) {
          try {
            userType = JSON.parse(rawUserType);
          } catch {}
        }
      }
      if (!userType) return false;
      const byName = typeof userType.name === 'string' && userType.name.toUpperCase() === 'DRIVER';
      const byId = userType.id === 5;
      return byName || byId;
    } catch {
      return false;
    }
  }

  // Handle incoming location update
  async handleLocationUpdate(location) {
    console.log('Handling location update:', location);
    this.showToast('Processing location update', { autoClose: 2000 });
    
    // Do not call API if user is not a Driver
    if (!this.isDriverUser()) {
      console.log('Skipping location update: current user is not a DRIVER');
      return;
    }
    
    if (this.activeTrips.size > 0) {
      const locationData = {
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        timestamp: new Date(location.timestamp).toISOString()
      };

      // Send location update to server for each active trip
      for (const tripId of Array.from(this.activeTrips)) {
        try {
          console.log('Sending location update to server for trip:', tripId, locationData);
          this.showToast(`Sending location update for trip ${tripId}`, { autoClose: 2000 });
          
          const meta = this.activeTripMeta.get(tripId) || this.activeTripMeta.get(Number(tripId)) || this.activeTripMeta.get(String(tripId)) || {};
          const isSpotRental = !!meta.isSpotRental;
          
          const response = await client.post(ENDPOINTS.updateDriverLocation, {
            tripId: tripId,
            latitude: locationData.latitude,
            longitude: locationData.longitude,
            accuracy: locationData.accuracy,
            timestamp: locationData.timestamp,
            type: isSpotRental ? 'SPOT RENTAL' : 'ETS',
            thirdPartyTripId: isSpotRental ? (meta.thirdPartyTripId ?? null) : null,
            channelName: meta.channelName ?? null
          });

          const isSuccess = response?.status === 200 || response?.data?.status === 200;
          const isEnded = !!(response?.data?.isTripEnded || response?.data?.tripEnded);

          if (isEnded) {
            console.log(`Trip ${tripId} has ended. Stopping location tracking.`);
            this.stopTracking(tripId);
          } else if (isSuccess) {
            console.log('Location updated successfully for trip:', tripId);
            this.showToast(`Location updated successfully for trip ${tripId}`, { autoClose: 2000 });
          } else {
            console.error('Failed to update location for trip:', tripId, response?.data);
            this.showToast(`Failed to update location for trip ${tripId}`, { autoClose: 5000 });
          }
        } catch (error) {
          console.error('Error sending location to server for trip:', tripId, error);
          const errorMsg = error?.response?.data?.message || error?.response?.data?.error || error?.response?.data || error?.message || '';
          const isEndedError = error?.response?.status === 410 || 
            (error?.response?.status === 400 && (
              error?.response?.data?.isTripEnded ||
              String(errorMsg).toLowerCase().includes('ended') ||
              String(errorMsg).toLowerCase().includes('complete') ||
              String(errorMsg).toLowerCase().includes('cancelled') ||
              String(errorMsg).toLowerCase().includes('dropped')
            ));

          if (isEndedError) {
            console.log(`Trip ${tripId} reported ended by server. Stopping location tracking.`);
            this.stopTracking(tripId);
          } else {
            this.showToast(`Error updating location: ${error.message}`, { autoClose: 5000 });
          }
        }
      }
    } else {
      console.log('No active trips, skipping location update');
      this.showToast('No active trips to update location for', { autoClose: 2000 });
    }
  }

  // Start tracking location for a trip (optionally with metadata)
  startTracking(tripId, metadata = {}) {
    // Several screens start tracking for the same trip; only the first call starts it, later calls just merge metadata.
    if (!this.activeTrips.has(tripId)) {
      console.log('Starting location tracking for trip:', tripId);
    }
    this.activeTrips.add(tripId);
    // Ensure a meta object exists
    const existing = this.activeTripMeta.get(tripId) || {};
    this.activeTripMeta.set(tripId, { ...existing, ...metadata });
    // Persist active trips to storage
    try {
      localStorage.setItem('activeTripIds', JSON.stringify(Array.from(this.activeTrips)));
      localStorage.setItem('activeTripMeta', JSON.stringify(Object.fromEntries(this.activeTripMeta)));
    } catch (e) {
      console.error('Failed to persist active trips:', e);
    }
    if (!this.isTracking) {
      this.startLocationUpdates();
    }
  }

  // Stop tracking location for a trip
  stopTracking(tripId) {
    if (!tripId) return;
    console.log('Stopping location tracking for trip:', tripId);
    this.activeTrips.delete(tripId);
    this.activeTrips.delete(Number(tripId));
    this.activeTrips.delete(String(tripId));
    this.activeTripMeta.delete(tripId);
    this.activeTripMeta.delete(Number(tripId));
    this.activeTripMeta.delete(String(tripId));
    // Persist active trips to storage
    try {
      localStorage.setItem('activeTripIds', JSON.stringify(Array.from(this.activeTrips)));
      localStorage.setItem('activeTripMeta', JSON.stringify(Object.fromEntries(this.activeTripMeta)));
    } catch (e) {
      console.error('Failed to persist active trips:', e);
    }
    if (this.activeTrips.size === 0) {
      this.stopLocationUpdates();
    }
  }

  // Get current location
  getCurrentLocation() {
    console.log('Getting current location:', {
      isAndroidWebView: this.isAndroidWebView,
      locationPermissionGranted: this.locationPermissionGranted
    });

    return new Promise((resolve, reject) => {
      if (this.isAndroidWebView) {
        // Use Android bridge for location
        if (this.locationPermissionGranted) {
          console.log('Requesting location from Android WebView');
          window.Android.getCurrentLocation();
          // The result will come through onLocationUpdate callback
          window.tempLocationResolver = resolve;
          window.tempLocationRejecter = reject;
          
          // Set a timeout to reject if we don't get a response
          setTimeout(() => {
            if (window.tempLocationResolver) {
              console.log('Location request timed out');
              reject(new Error('Location request timed out'));
              delete window.tempLocationResolver;
              delete window.tempLocationRejecter;
            }
          }, LOCATION_CONFIG.TIMEOUT);
        } else {
          console.log('Requesting location permission from Android');
          window.Android.requestLocationPermission();
          reject(new Error('Location permission not granted'));
        }
      } else {
        // Use standard web geolocation API
        if (!navigator.geolocation) {
          reject(new Error('Geolocation is not supported by your browser'));
          return;
        }

        const options = {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        };

        const success = (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            speed: position.coords.speed,
            heading: position.coords.heading,
            timestamp: new Date().toISOString()
          };
          console.log('Location obtained:', location);
          resolve(location);
        };

        const error = (err) => {
          console.error('Error getting location:', err);
          let errorMessage = 'Failed to get location';
          
          switch (err.code) {
            case err.PERMISSION_DENIED:
              errorMessage = 'Location permission denied. Please enable location services in your device settings.';
              break;
            case err.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable. Please check your GPS settings.';
              break;
            case err.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again.';
              break;
            default:
              errorMessage = 'An unknown error occurred while getting location.';
          }
          
          reject(new Error(errorMessage));
        };

        navigator.geolocation.getCurrentPosition(success, error, options);
      }
    });
  }

  // Send location to server
  async sendLocationToServer(location, tripId) {
    try {
      // Do not call API if user is not a Driver
      if (!this.isDriverUser()) {
        console.log('Skipping API call: current user is not a DRIVER');
        return false;
      }

      console.log('Sending location update for trip:', tripId, location);
      const meta = this.activeTripMeta.get(tripId) || {};
      const isSpotRental = !!meta.isSpotRental;
      
      const response = await client.post(ENDPOINTS.updateDriverLocation, {
        tripId: tripId,
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        timestamp: location.timestamp,
        type: isSpotRental ? 'SPOT RENTAL' : 'ETS',
        thirdPartyTripId: isSpotRental ? (meta.thirdPartyTripId ?? null) : null,
        channelName: meta.channelName ?? null
      });

      const isSuccess = response?.status === 200 || response?.data?.status === 200;
      const isEnded = !!(response?.data?.isTripEnded || response?.data?.tripEnded);

      if (isEnded) {
        console.log(`Trip ${tripId} has ended. Stopping location tracking.`);
        this.stopTracking(tripId);
        return true;
      }

      if (isSuccess) {
        console.log('Location updated successfully for trip:', tripId);
        // Show toast in Android WebView
        if (window.Android && window.Android.showToast) {
          window.Android.showToast('Location updated successfully');
        }
        return true;
      } else {
        console.error('Failed to update location for trip:', tripId, response?.data);
        if (window.Android && window.Android.showToast) {
          window.Android.showToast('Failed to update location');
        }
        return false;
      }
    } catch (error) {
      console.error('Error sending location to server for trip:', tripId, error);
      const errorMsg = error?.response?.data?.message || error?.response?.data?.error || error?.response?.data || error?.message || '';
      const isEndedError = error?.response?.status === 410 || 
        (error?.response?.status === 400 && (
          error?.response?.data?.isTripEnded ||
          String(errorMsg).toLowerCase().includes('ended') ||
          String(errorMsg).toLowerCase().includes('complete') ||
          String(errorMsg).toLowerCase().includes('cancelled') ||
          String(errorMsg).toLowerCase().includes('dropped')
        ));

      if (isEndedError) {
        console.log(`Trip ${tripId} reported ended by server. Stopping location tracking.`);
        this.stopTracking(tripId);
      }
      if (window.Android && window.Android.showToast) {
        window.Android.showToast('Error updating location: ' + error.message);
      }
      return false;
    }
  }

  // Start location updates
  startLocationUpdates() {
    if (this.isTracking) {
      console.log('Location tracking already active');
      this.showToast('Location tracking already active', { autoClose: 2000 });
      return;
    }
    
    console.log('Starting location tracking with active trips:', Array.from(this.activeTrips));
    this.showToast(`Starting location tracking for ${this.activeTrips.size} active trips`, { autoClose: 2000 });
    
    this.isTracking = true;

    if (this.isAndroidWebView) {
      // For Android WebView, use the same interval logic but get location from Android
      console.log('Starting Android WebView location updates with interval:', LOCATION_CONFIG.UPDATE_INTERVAL);
      this.showToast(`Starting Android WebView location updates every ${LOCATION_CONFIG.UPDATE_INTERVAL/1000} seconds`, { autoClose: 2000 });
      
      this.updateLocations(); // Initial update
      
      if (this.intervalId) {
        console.log('Clearing existing interval');
        this.showToast('Clearing existing location update interval', { autoClose: 2000 });
        clearInterval(this.intervalId);
      }
      
      this.intervalId = setInterval(() => {
        console.log('Running scheduled location update in Android WebView');
        this.showToast('Running scheduled location update', { autoClose: 2000 });
        this.updateLocations();
      }, LOCATION_CONFIG.UPDATE_INTERVAL);
    } else {
      // For web browsers, use the interval-based updates
      console.log('Starting web browser location updates with interval:', LOCATION_CONFIG.UPDATE_INTERVAL);
      this.showToast(`Starting web browser location updates every ${LOCATION_CONFIG.UPDATE_INTERVAL/1000} seconds`, { autoClose: 2000 });
      
      this.updateLocations(); // Initial update
      
      if (this.intervalId) {
        console.log('Clearing existing interval');
        this.showToast('Clearing existing location update interval', { autoClose: 2000 });
        clearInterval(this.intervalId);
      }
      
      this.intervalId = setInterval(() => {
        console.log('Running scheduled location update');
        this.showToast('Running scheduled location update', { autoClose: 2000 });
        this.updateLocations();
      }, LOCATION_CONFIG.UPDATE_INTERVAL);
    }

    console.log('Location tracking started successfully');
    this.showToast('Location tracking started successfully', { autoClose: 2000 });
  }

  // Stop location updates
  stopLocationUpdates() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.isAndroidWebView) {
      console.log('Stopping Android WebView location updates');
      // window.Android.stopLocationUpdates();
    }
    this.isTracking = false;
    console.log('Location tracking stopped');
  }

  // Update locations for all active trips
  async updateLocations() {
    try {
      let position;
      
      if (this.isAndroidWebView) {
        // For Android WebView, get location from Android
        position = await new Promise((resolve, reject) => {
          // Set up a one-time handler for the location
          const locationHandler = (location) => {
            console.log('Received location from Android:', location);
            // Remove the handler after receiving the location
            window.onLocationUpdate = null;
            resolve(location);
          };
          
          // Set the handler
          window.onLocationUpdate = locationHandler;
          
          // Request a new location from Android
          if (window.Android) {
            window.Android.getCurrentLocation();
          } else {
            reject(new Error('Android interface not found'));
          }
          
          // Set a timeout in case we don't get a response
          setTimeout(() => {
            if (window.onLocationUpdate === locationHandler) {
              window.onLocationUpdate = null;
              reject(new Error('Location request timed out'));
            }
          }, 5000);
        });
      } else {
        // For web browser, use the geolocation API
        position = await this.getCurrentLocation();
      }

      if (position) {
        // Handle both Android and web browser location formats
        const locationData = {
          latitude: position.coords?.latitude || position.latitude,
          longitude: position.coords?.longitude || position.longitude,
          accuracy: position.coords?.accuracy || position.accuracy,
          timestamp: position.timestamp || Date.now()
        };
        
        // Validate the location data
        if (typeof locationData.latitude !== 'number' || typeof locationData.longitude !== 'number') {
          throw new Error('Invalid location data received');
        }
        
        await this.handleLocationUpdate(locationData);
      }
    } catch (error) {
      console.error('Error updating locations:', error);
      this.showToast('Error updating locations: ' + error.message, { autoClose: 2000 });
    }
  }

  // Get last known location
  getLastKnownLocation() {
    return this.lastLocation;
  }
}

// Create a singleton instance
const locationTrackingService = new LocationTrackingService();

// Initialize the Android bridge if available
locationTrackingService.initAndroidBridge();

export default locationTrackingService;